import React from 'react';
import Button from '../../../components/ui/Button';
import { useReport } from '../hooks/useReport';

const CARD = 'rounded-md border border-dash-border bg-dash-card p-4';
const PAGE_SIZE = 25;

const fmtDate = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const ComplianceSection = ({ filters }) => {
  const [page, setPage] = React.useState(1);
  const filtersKey = JSON.stringify(filters);
  // Reset to page 1 whenever the filters change.
  React.useEffect(() => setPage(1), [filtersKey]);

  const params = React.useMemo(() => ({ ...filters, page, pageSize: PAGE_SIZE }), [filters, page]);
  const { data, loading } = useReport('/reports/mis/records', params);
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-dash-foreground">Compliance / Records</h2>
      <div className={CARD}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-dash-foreground">Vendor Records</h3>
          <span className="text-[11px] text-dash-muted-foreground">{total.toLocaleString()} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-dash-border text-dash-muted-foreground">
                <th className="py-1 pr-2 text-left font-medium">Code</th>
                <th className="py-1 px-2 text-left font-medium">Company</th>
                <th className="py-1 px-2 text-left font-medium">Status</th>
                <th className="py-1 px-2 text-left font-medium">Origin</th>
                <th className="py-1 px-2 text-left font-medium">Region</th>
                <th className="py-1 px-2 text-left font-medium">State</th>
                <th className="py-1 px-2 text-left font-medium">Currency</th>
                <th className="py-1 px-2 text-left font-medium">MSME</th>
                <th className="py-1 pl-2 text-left font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={9} className="py-3 text-center text-dash-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="py-3 text-center text-dash-muted-foreground">No records</td></tr>
              ) : (
                rows.map((v) => (
                  <tr key={v.id} className={`border-b border-dash-border/50 ${v.is_deleted ? 'opacity-70' : ''}`}>
                    <td className="py-1 pr-2 text-dash-foreground">{v.vendor_code || 'N/A'}</td>
                    <td className="py-1 px-2 text-dash-foreground">
                      {v.company_name}
                      {v.is_deleted && (
                        <span className="ml-1.5 rounded bg-dash-destructive/15 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-dash-destructive">
                          deleted
                        </span>
                      )}
                    </td>
                    <td className="py-1 px-2 text-dash-muted-foreground">{v.status}</td>
                    <td className="py-1 px-2 text-dash-muted-foreground">{v.country_origin}</td>
                    <td className="py-1 px-2 text-dash-muted-foreground">{v.region || '—'}</td>
                    <td className="py-1 px-2 text-dash-muted-foreground">{v.registered_state}</td>
                    <td className="py-1 px-2 text-dash-muted-foreground">{v.currency}</td>
                    <td className="py-1 px-2 text-dash-muted-foreground">{v.msme_status}</td>
                    <td className="py-1 pl-2 text-dash-muted-foreground">{fmtDate(v.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-dash-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="xs" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </Button>
            <Button variant="outline" size="xs" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              Next
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceSection;
